import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const getProxyUrl = () => {
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  return `https://${projectId}.supabase.co/functions/v1/decoprof-orders-proxy`;
};

const getHeaders = () => {
  const sessionToken = localStorage.getItem('auth_session_token');
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  return {
    'Content-Type': 'application/json',
    'x-session-token': sessionToken || '',
    'apikey': anonKey,
    'Authorization': `Bearer ${anonKey}`,
  };
};

export interface KleurenOrder {
  ordernumber: string;
  producttitle: string;
  customfieldcolor: string;
  status: string;
  customer_firstname: string;
  customer_lastname: string;
  brand_title?: string;
  quantity?: number;
  createdat?: string;
  sku?: string;
}

export interface KleurenOrdersFilters {
  search?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}

export const useKleurenOrdersList = (filters: KleurenOrdersFilters = {}) => {
  return useQuery({
    queryKey: ['kleuren-orders', filters],
    queryFn: async () => {
      const params = new URLSearchParams({ action: 'list' });
      if (filters.status) params.set('status', filters.status);
      if (filters.search) params.set('search', filters.search);
      if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.set('dateTo', filters.dateTo);
      params.set('limit', String(filters.limit || 50));
      if (filters.offset) params.set('offset', String(filters.offset));

      const res = await fetch(`${getProxyUrl()}?${params}`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Fout bij ophalen orders');
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Onbekende fout');
      return json as { success: boolean; data: KleurenOrder[]; meta: { total: number; limit: number; offset: number } };
    },
  });
};

export const useKleurenOrderDetail = (ordernumber: string | null) => {
  return useQuery({
    queryKey: ['kleuren-order-detail', ordernumber],
    queryFn: async () => {
      const params = new URLSearchParams({ action: 'detail', ordernumber: ordernumber! });
      const res = await fetch(`${getProxyUrl()}?${params}`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Fout bij ophalen order detail');
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Onbekende fout');
      return json.data;
    },
    enabled: !!ordernumber,
  });
};

export const useMarkPrinted = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ordernumbers: string[]) => {
      const res = await fetch(getProxyUrl(), {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ action: 'mark_printed', ordernumbers }),
      });
      if (!res.ok) throw new Error('Fout bij markeren als afgedrukt');
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Onbekende fout');
      return json.data;
    },
    onSuccess: (_, ordernumbers) => {
      toast.success(`${ordernumbers.length} order(s) gemarkeerd als afgedrukt`);
      queryClient.invalidateQueries({ queryKey: ['kleuren-orders'] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};

export const useAddNote = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ordernumber, message, sender_name }: { ordernumber: string; message: string; sender_name?: string }) => {
      const res = await fetch(getProxyUrl(), {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ action: 'add_note', ordernumber, message, sender_name }),
      });
      if (!res.ok) throw new Error('Fout bij toevoegen notitie');
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Onbekende fout');
      return json.data;
    },
    onSuccess: () => {
      toast.success('Notitie toegevoegd');
      queryClient.invalidateQueries({ queryKey: ['kleuren-order-detail'] });
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};
